import { FIELD, VIEW } from '../core/config.js';
import { drawFrame, getFloor } from './backdrop.js';
import { drawDoh, drawDohHealth } from './doh-sprite.js';
import { drawText, measureText } from './font.js';
import { UI } from './palette.js';
import {
  drawBalls,
  drawBolts,
  drawBricks,
  drawCapsules,
  drawDohShots,
  drawParticles,
  drawPaddle,
} from './sprites.js';

/**
 * Owns the canvas. Draws one frame of the session, scaling the fixed 448x640
 * playfield up to whatever size the layout gives us.
 */

export function createRenderer(canvas) {
  const ctx = canvas.getContext('2d', { alpha: false });
  let scale = 1;

  function resize() {
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2.5);
    const width = Math.max(1, Math.round(rect.width * dpr));
    const height = Math.max(1, Math.round(rect.height * dpr));
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }
    scale = width / VIEW.W;
  }

  /** Map a client-space x onto the logical playfield. */
  function toLogicalX(clientX) {
    const rect = canvas.getBoundingClientRect();
    if (!rect.width) return VIEW.W / 2;
    return ((clientX - rect.left) / rect.width) * VIEW.W;
  }

  function draw(session, { shake = { x: 0, y: 0 } } = {}) {
    resize();
    ctx.setTransform(scale, 0, 0, scale, 0, 0);
    ctx.imageSmoothingEnabled = false;

    ctx.fillStyle = UI.void;
    ctx.fillRect(0, 0, VIEW.W, VIEW.H);
    ctx.drawImage(getFloor(session.backdropIndex), 0, 0);

    ctx.save();
    ctx.translate(shake.x, shake.y);

    if (session.field) drawBricks(ctx, session.field);
    if (session.doh) drawDoh(ctx, session.doh);
    drawCapsules(ctx, session.capsules);
    drawBolts(ctx, session.bolts);
    drawDohShots(ctx, session.dohShots);
    if (session.paddleVisible) drawPaddle(ctx, session.paddle);
    drawBalls(ctx, session.balls);
    drawParticles(ctx, session.particles.items);

    ctx.restore();

    drawFrame(ctx, {
      warpOpen: session.warpOpen,
      warpPhase: session.warpPhase,
      paddleY: session.paddle.y,
    });

    if (session.doh) drawDohHealth(ctx, session.doh);
    if (session.banner) drawBanner(ctx, session.banner);
    if (session.toast) drawToast(ctx, session.toast, Boolean(session.doh));
  }

  return { ctx, draw, resize, toLogicalX, get scale() { return scale; } };
}

function drawBanner(ctx, banner) {
  const centerY = 300;
  const titleScale = 4;
  const width = Math.max(
    measureText(banner.title, titleScale),
    banner.sub ? measureText(banner.sub, 2) : 0,
  );

  // A solid plate keeps the text readable over any brick pattern.
  const boxW = width + 44;
  const boxH = banner.sub ? 74 : 48;
  const boxX = VIEW.W / 2 - boxW / 2;
  const boxY = centerY - 18;

  ctx.fillStyle = 'rgba(8,10,14,0.86)';
  ctx.fillRect(boxX, boxY, boxW, boxH);
  ctx.fillStyle = banner.accent || UI.accent;
  ctx.fillRect(boxX, boxY, boxW, 2);
  ctx.fillRect(boxX, boxY + boxH - 2, boxW, 2);

  drawText(ctx, banner.title, VIEW.W / 2, boxY + 12, {
    scale: titleScale,
    color: banner.color || UI.ink,
    align: 'center',
  });

  if (banner.sub) {
    drawText(ctx, banner.sub, VIEW.W / 2, boxY + 48, {
      scale: 2,
      color: UI.inkDim,
      align: 'center',
    });
  }
}

function drawToast(ctx, toast, hasBoss) {
  const alpha = Math.min(1, toast.timer * 2.2);
  // Sits below the boss health bar when DOH is on the field.
  const y = FIELD.top + (hasBoss ? 26 : 12);
  const width = Math.min(FIELD.width - 8, measureText(toast.text, 2) + 20);

  ctx.globalAlpha = alpha;
  ctx.fillStyle = 'rgba(8,10,14,0.82)';
  ctx.fillRect(VIEW.W / 2 - width / 2, y, width, 18);
  drawText(ctx, toast.text, VIEW.W / 2, y + 4, { scale: 2, color: UI.accentAlt, align: 'center' });
  ctx.globalAlpha = 1;
}
